import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Week, WeekWithStats, Group, Block } from '@/features/stm/lib/types'
import { isGroupInWorkWeek } from '@/features/stm/lib/date-utils'
import { toast } from 'sonner'
import { format } from 'date-fns'

import { authFetch } from '@/shared/lib/auth-fetch'

async function stmFetch(url: string, init?: RequestInit) {
  const res = await authFetch(url, init)
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status}`)
  }
  return res.json()
}

/** Calendar week from API (includes overlappingExceptions, hasHardBlock, hasSoftBlock) */
export interface CalendarWeekResponse extends Week {
  overlappingExceptions?: Array<{
    id: number
    start_date: string
    end_date: string
    capacity: number
    reason: string | null
    severity: string | null
  }>
  hasHardBlock?: boolean
  hasSoftBlock?: boolean
}

/**
 * Fetch calendar weeks from GET /api/stm/weeks (proxies to backend calendar/weeks).
 * Uses startDate (default: first of current month) and months=1.
 */
export function useStmWeeks(
  regionId: number | undefined | null,
  startDate?: string,
  months: number = 1
) {
  const start = startDate ?? format(new Date(), 'yyyy-MM-01')
  return useQuery({
    queryKey: ['stm', 'calendar', 'weeks', regionId ?? '', start, months],
    queryFn: () =>
      stmFetch(
        `/api/stm/weeks?regionId=${regionId}&startDate=${start}&months=${months}`
      ) as Promise<CalendarWeekResponse[]>,
    enabled: !!regionId,
  })
}

const STM_GROUPS_STALE_MS = 2 * 60 * 1000 // 2 min – reuse when switching Calendar <-> Groups

export function useStmGroups(
  regionId: number | undefined | null,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled !== false && !!regionId
  return useQuery({
    queryKey: ['stm', 'groups', regionId ?? ''],
    queryFn: () => stmFetch(`/api/stm/groups?regionId=${regionId}`) as Promise<Group[]>,
    enabled,
    refetchOnWindowFocus: false,
    staleTime: STM_GROUPS_STALE_MS,
  })
}

/** Fetch a single group by id. Use for group detail page when group is not in list cache. */
export function useStmGroupById(
  groupId: string | null,
  regionId?: number | null
) {
  return useQuery({
    queryKey: ['stm', 'group', groupId ?? '', regionId ?? ''],
    queryFn: () =>
      stmFetch(
        `/api/stm/groups/${encodeURIComponent(groupId!)}${regionId ? `?regionId=${regionId}` : ''}`
      ) as Promise<Group>,
    enabled: !!groupId,
    staleTime: STM_GROUPS_STALE_MS,
    refetchOnWindowFocus: false,
  })
}

/**
 * Get one group for the detail page. Uses list cache (groups or calendar) when available so
 * no request is needed when navigating from Groups/Calendar tab; otherwise fetches single group by id.
 */
export function useStmGroup(
  groupId: string | null,
  regionId: number | undefined | null
) {
  const start = format(new Date(), 'yyyy-MM-01')
  const listQuery = useStmGroups(regionId ?? null, { enabled: false })
  const calendarQuery = useQuery({
    queryKey: ['stm', 'calendar', 'combined', regionId ?? '', start, 1],
    queryFn: () =>
      stmFetch(
        `/api/stm/calendar?regionId=${regionId}&startDate=${start}&months=1`
      ) as Promise<CalendarDataResponse>,
    enabled: false,
  })
  const groupsFromCache = listQuery.data ?? calendarQuery.data?.groups ?? null
  const groupFromCache =
    groupId && Array.isArray(groupsFromCache)
      ? groupsFromCache.find((g) => g.id === groupId) ?? null
      : null

  const byIdQuery = useStmGroupById(groupFromCache ? null : groupId, regionId ?? null)

  const group = groupFromCache ?? byIdQuery.data ?? null
  const isLoading = !groupFromCache && byIdQuery.isLoading
  const error = byIdQuery.error

  return { data: group, isLoading, error }
}

export function useStmBlocks(regionId: number | undefined | null) {
  return useQuery({
    queryKey: ['stm', 'blocks', regionId ?? ''],
    queryFn: () => stmFetch(`/api/stm/blocks?regionId=${regionId}`) as Promise<Block[]>,
    enabled: !!regionId,
  })
}

/**
 * Combined calendar response: weeks + groups in one request.
 * Use for the Volunteers calendar tab to avoid two round-trips.
 */
export interface CalendarDataResponse {
  weeks: CalendarWeekResponse[]
  groups: Group[]
}

/**
 * Fetch combined calendar data (weeks + groups) in one request.
 * Use this for the Volunteers calendar tab instead of useStmWeeks + useStmGroups.
 */
function useStmCalendar(
  regionId: number | undefined | null,
  startDate?: string,
  months: number = 1
) {
  const start = startDate ?? format(new Date(), 'yyyy-MM-01')
  return useQuery({
    queryKey: ['stm', 'calendar', 'combined', regionId ?? '', start, months],
    queryFn: () =>
      stmFetch(
        `/api/stm/calendar?regionId=${regionId}&startDate=${start}&months=${months}`
      ) as Promise<CalendarDataResponse>,
    enabled: !!regionId,
    staleTime: STM_GROUPS_STALE_MS,
  })
}

/**
 * Groups for the Groups tab. Uses the combined calendar endpoint (weeks + groups)
 * so one request loads the list; calendar cache is reused when switching from Calendar tab.
 */
export function useStmGroupsForTab(
  regionId: number | undefined | null,
  startDate?: string
) {
  const start = startDate ?? format(new Date(), 'yyyy-MM-01')
  const calendarQuery = useStmCalendar(regionId, start, 1)
  const data = calendarQuery.data?.groups ?? []
  /** True while region is set but combined calendar response has not arrived yet (avoid showing empty as “no groups”). */
  const isLoading =
    !!regionId &&
    (calendarQuery.isPending ||
      (calendarQuery.isFetching && calendarQuery.data === undefined))
  return {
    data,
    isLoading,
    error: calendarQuery.error,
  }
}

/**
 * Weeks with stats: uses calendar/weeks (which includes capacity, hasHardBlock, hasSoftBlock)
 * plus groups for filledSpots, fillRate, etc.
 * Uses combined GET /api/stm/calendar when available to reduce load time.
 */
export function useStmWeeksWithStats(
  regionId: number | undefined | null,
  startDate?: string,
  months: number = 1
) {
  const start = startDate ?? format(new Date(), 'yyyy-MM-01')
  const { data: calendarData, isLoading, error } = useStmCalendar(regionId, start, months)

  const weeks = calendarData?.weeks ?? []
  const groups = calendarData?.groups ?? []

  const weeksWithStats: WeekWithStats[] = weeks.map((week) => {
    const overlappingGroups = groups.filter(
      (g) =>
        g.status !== 'Cancelled' &&
        isGroupInWorkWeek(g.start_date, g.end_date, week.week_start, week.week_end)
    )
    const filledSpots = overlappingGroups.reduce((s, g) => s + g.participants, 0)
    const availableSpots = Math.max(0, week.capacity - filledSpots)
    const fillRate = week.capacity > 0 ? (filledSpots / week.capacity) * 100 : 0
    const hasHardBlock = week.hasHardBlock ?? false
    const hasSoftBlock = week.hasSoftBlock ?? false
    const overlappingBlocks = (week.overlappingExceptions || [])
      .filter((e) => e.capacity === 0 || e.severity)
      .map((e) => ({
        id: String(e.id),
        start_date: e.start_date,
        end_date: e.end_date,
        reason: e.reason ?? '',
        severity: (e.severity ?? 'Hard Block') as Block['severity'],
        created_at: '',
        updated_at: '',
      }))
    return {
      ...week,
      filledSpots,
      availableSpots,
      fillRate,
      groups: overlappingGroups,
      blocks: overlappingBlocks,
      hasHardBlock,
      hasSoftBlock,
    }
  })

  return { data: weeksWithStats, isLoading, error }
}

export function useStmUpdateWeekCapacity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      week_start,
      week_end,
      capacity,
      regionId,
    }: {
      week_start: string
      week_end: string
      capacity: number
      regionId: number
    }) => {
      await stmFetch(`/api/stm/capacity-exceptions?regionId=${regionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: week_start,
          end_date: week_end,
          capacity,
        }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Capacity updated')
    },
    onError: (e: Error) => toast.error('Failed to update capacity: ' + e.message),
  })
}

